import { memo, useCallback } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";
import AutoplayVideo from "components/AutoplayVideo";
import Carousel from "components/Carousel";
import { CarouselNextButton, CarouselSlide, CarouselStepper } from "components/Carousel/components";
import useCarousel from "components/Carousel/hooks/useCarousel";
import DialogContent from "components/DialogV2/DialogContent";
import Typography from "components/Typography";
import type { TypographyProps } from "components/Typography";

export const AthleteVelocityAnnouncementContent = styled(DialogContent)(({ theme }) => ({
  "&[data-size=md]": {
    padding: theme.spacing(5.5, 3, 3, 3),
  },
}));

export const AthleteVelocityAnnouncementCarousel = styled(Carousel)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
}));

export const AthleteVelocityAnnouncementFooter = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
}));

export const AthleteVelocityAnnouncementVideo = styled(AutoplayVideo)({
  width: "358px",
  maxWidth: "100%",
  borderRadius: "20px",
});

export const AthleteVelocityAnnouncementDescription = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

/** Sits under the dialog title, so it is the third heading level. */
export function AthleteVelocityAnnouncementHeading(props: TypographyProps) {
  return <Typography variant="TextXXL" component="h3" fontWeight="fontWeightMedium" {...props} />;
}

const AthleteVelocityAnnouncementSlideRoot = styled(CarouselSlide)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
  textAlign: "center",
}));

interface AthleteVelocityAnnouncementSlideProps {
  video: string;
  videoAlt: string;
  heading: string;
  /** @description The description paragraphs. */
  children: ReactNode;
}

export const AthleteVelocityAnnouncementSlide = memo((props: AthleteVelocityAnnouncementSlideProps) => {
  return (
    <AthleteVelocityAnnouncementSlideRoot>
      <AthleteVelocityAnnouncementHeading>{props.heading}</AthleteVelocityAnnouncementHeading>
      <AthleteVelocityAnnouncementVideo src={props.video} alt={props.videoAlt} />
      <CarouselStepper />
      <AthleteVelocityAnnouncementDescription>{props.children}</AthleteVelocityAnnouncementDescription>
    </AthleteVelocityAnnouncementSlideRoot>
  );
});

const AthleteVelocityAnnouncementNextButtonRoot = styled(CarouselNextButton)(({ theme }) => ({
  minWidth: "142px",
}));

interface AthleteVelocityAnnouncementNextButtonProps {
  /** @description Called instead of moving on when the last slide is showing. */
  onFinish: () => void;
}

/**
 * Stays the same element on the last slide, reading "Got it", so keyboard focus is not lost
 * when the label changes.
 */
export const AthleteVelocityAnnouncementNextButton = memo((props: AthleteVelocityAnnouncementNextButtonProps) => {
  const { onFinish } = props;
  const { t } = useTranslation();
  const { isLastStep } = useCarousel();

  const handleOnClick = useCallback(() => {
    if (isLastStep) onFinish();
  }, [isLastStep, onFinish]);

  return (
    <AthleteVelocityAnnouncementNextButtonRoot onClick={handleOnClick} size="large">
      {isLastStep ? t("global.got_it_label") : t("global.next_button_text")}
    </AthleteVelocityAnnouncementNextButtonRoot>
  );
});
